/**
 * Get Rates (Pages) redirect / execute normalization — layout parity helpers.
 * Imported by PagesContainerUtils.js
 */

import { flattenFormPayloadForPagesExecute } from "./pagesAutomationUtils";
import {
  isRatesResultsConfig,
  isRatesResultsPageId,
  readPagesFeatureFlags,
  isTruthyPagesFlag,
} from "./pagesFeatureConfig";
import { getDateFormat } from "../../Utils/UserFunctions";

/**
 * Legacy snapshot key. Do not write it. A previous visit's shipment was
 * painted back onto Add Shipment from this key. Live Get Rates / Ship Now
 * context stays on pageFlows for the current visit.
 */
const GET_RATES_SESSION_STORAGE_KEY = "shipcrm_pages_rates_context_response";
const RATES_RESULTS_REGISTRY_KEY = "shipcrm_pages_rates_results_keys";

/** Search box value is form state only — must not persist in pages.params / session (stale 222766 on reload). */
export const EPHEMERAL_PAGE_PARAM_KEYS = [
  "search_by_reference",
  "search_by_reference_error",
];

export const omitEphemeralPagesParams = (params = {}) => {
  if (params == null || typeof params !== "object" || Array.isArray(params)) {
    return {};
  }
  const next = { ...params };
  EPHEMERAL_PAGE_PARAM_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
};

/**
 * Same-page execute hydrate: each key present on this execute's To Mapping
 * replaces the prior runtime value (including [] / "").
 * No field-name or config-name allowlists — driven only by mapped outputs.
 * Get Rates redirect still uses ensureGetRatesMarkupOutputResponse + prefer-rich.
 */
export const mergeSamePageExecuteOutputs = (
  priorRuntime = {},
  executeOutputs = {}
) => {
  const prior =
    priorRuntime &&
    typeof priorRuntime === "object" &&
    !Array.isArray(priorRuntime)
      ? priorRuntime
      : {};
  const mapped =
    executeOutputs &&
    typeof executeOutputs === "object" &&
    !Array.isArray(executeOutputs)
      ? executeOutputs
      : {};
  const next = { ...prior };
  Object.keys(mapped).forEach((key) => {
    let val = mapped[key];
    if (val === "null") {
      val = null;
    }
    next[key] = val;
  });
  return next;
};

/** Mirror this execute's mapped keys into host pages.params (same-page). */
export const applyMappedExecuteOutputsToParams = (
  params = {},
  executeOutputs = {}
) => {
  const next = omitEphemeralPagesParams(params);
  const mapped =
    executeOutputs &&
    typeof executeOutputs === "object" &&
    !Array.isArray(executeOutputs)
      ? executeOutputs
      : {};
  Object.keys(mapped).forEach((key) => {
    if (EPHEMERAL_PAGE_PARAM_KEYS.includes(key)) {
      return;
    }
    let val = mapped[key];
    if (val === "null") {
      val = null;
    }
    next[key] = val;
  });
  // Clone preloads via record_id then must save as CREATE — drop ids after hydrate.
  if (
    isTruthyPagesFlag(next.is_clone) ||
    isTruthyPagesFlag(next.pages_clone_mode)
  ) {
    delete next.record_id;
    delete next.record_ids;
    ["Package", "Packages", "package", "packages"].forEach((key) => {
      const rows = next[key];
      if (!Array.isArray(rows)) {
        return;
      }
      next[key] = rows.map((row) => {
        if (!row || typeof row !== "object" || Array.isArray(row) || row.id == null || row.id === "") {
          return row;
        }
        const copy = { ...row };
        delete copy.id;
        return copy;
      });
    });
  }
  return next;
};

/** DIRECT pages config api_names are underscored tokens (e.g. Tracking_disply_page). */
const looksLikePagesConfigurationApiName = (name) => {
  if (name == null || typeof name !== "string") {
    return false;
  }
  const trimmed = name.trim();
  if (!trimmed || trimmed === "SAME_PAGE") {
    return false;
  }
  if (/\s/.test(trimmed)) {
    return false;
  }
  return true;
};

const readRatesResultsRegistry = () => {
  try {
    const raw = sessionStorage.getItem(RATES_RESULTS_REGISTRY_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.map((v) => String(v)).filter(Boolean)
      : [];
  } catch (e) {
    return [];
  }
};

/**
 * Redux / route key after pages/search-by-id.
 * Prefer API configuration_api_name, then caller fallback (already resolved from toMap).
 */
export const resolvePagesTargetConfigKey = (
  resData,
  configApiNameFallback,
  pageId
) => {
  if (resData?.configuration_api_name) {
    return resData.configuration_api_name;
  }
  if (looksLikePagesConfigurationApiName(configApiNameFallback)) {
    return String(configApiNameFallback).trim();
  }
  return configApiNameFallback || pageId;
};

/**
 * SAME_WINDOW page redirect target config api_name from to_page_mapping.
 * Never returns the automation config name — callers must not fall back to that
 * for cross-page navigation (Tracking display must not become tracking_lookup).
 */
export const resolvePagesRedirectTargetConfigApiName = (toMap, pageId) => {
  if (toMap?.configuration_api_name) {
    return String(toMap.configuration_api_name).trim();
  }
  if (looksLikePagesConfigurationApiName(toMap?.page_name)) {
    return String(toMap.page_name).trim();
  }
  // Get Rates: display page_name may include spaces; use rates registry when id/flag matches.
  const ratesFlag = isTruthyPagesFlag(
    readPagesFeatureFlags(toMap || {}).rates_results_page
  );
  if (ratesFlag || (pageId && isRatesResultsPageId(pageId))) {
    const registered = readRatesResultsRegistry();
    if (registered.length > 0) {
      return registered[0];
    }
  }
  return null;
};

/** @deprecated Prefer resolvePagesRedirectTargetConfigApiName. */
export const resolveGetRatesRedirectConfigApiName = (toMap, pageId) =>
  resolvePagesRedirectTargetConfigApiName(toMap, pageId);

export const persistGetRatesExecuteResponse = () => {
  clearGetRatesSessionCache();
};

export const clearGetRatesSessionCache = () => {
  try {
    sessionStorage.removeItem(GET_RATES_SESSION_STORAGE_KEY);
    // Legacy keys from pre-refactor builds
    sessionStorage.removeItem("shipcrm_pages_get_rates_last_response");
    sessionStorage.removeItem("shipcrm_pages_last_response_Get_rates_config");
  } catch (e) {
    // ignore
  }
};

export const readGetRatesExecuteResponseFromSession = () => {
  clearGetRatesSessionCache();
  return {};
};

/**
 * Rates-results redirect must open its own route (not merge into host).
 * Driven by SHIPCRM_PAGES_FLAGS / session registry — not api_name literals.
 */
export const isGetRatesPageRedirectTarget = (toMap, pageId) => {
  const flags = readPagesFeatureFlags(toMap || {});
  if (isTruthyPagesFlag(flags.rates_results_page)) {
    return true;
  }
  if (pageId && isRatesResultsPageId(pageId)) {
    return true;
  }
  const apiName =
    (toMap?.configuration_api_name &&
      String(toMap.configuration_api_name).trim()) ||
    (looksLikePagesConfigurationApiName(toMap?.page_name)
      ? String(toMap.page_name).trim()
      : null);
  return Boolean(apiName && isRatesResultsConfig(apiName));
};

const toArrayIfObject = (value) => {
  if (value == null) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === "object") {
    return [value];
  }
  return [];
};

const shipObjRowRichnessScore = (row) => {
  const normalized = canonicalizeShipObjRowFieldNames(row);
  if (!normalized || typeof normalized !== "object") {
    return 0;
  }
  return SHIPOBJ_MARKUP_FIELDS.filter((key) => {
    const val = normalized[key];
    return val != null && String(val).trim() !== "";
  }).length;
};

const mergeShipObjArraysPreferRicher = (existing, incoming) => {
  const existingRows = toArrayIfObject(existing);
  const incomingRows = toArrayIfObject(incoming);
  if (existingRows.length === 0) {
    return incomingRows;
  }
  if (incomingRows.length === 0) {
    return existingRows;
  }
  // Later layer (e.g. SBR execute) wins field-by-field — do not keep account lookup shipObj
  // just because it has more populated markup columns than the shipment row from step.
  return [mergeShipObjRowPreferPrimary(existingRows[0], incomingRows[0])];
};

/** SBR / Add Shipment package rows keep cf_packages + description; Get Rates display rows are slim dims-only. */
export const packageRowsHaveSubformLookupOrDescription = (rows) => {
  const list = toArrayIfObject(rows);
  return list.some((row) => {
    if (!row || typeof row !== "object" || Array.isArray(row)) {
      return false;
    }
    const pkg = row.cf_packages;
    if (pkg != null && typeof pkg === "object" && !Array.isArray(pkg)) {
      return true;
    }
    const description =
      row.cf_description ?? row.description ?? row.Description;
    return description != null && String(description).trim() !== "";
  });
};

/** True when at least one row has a usable weight or L/W/H (not blank/zero-empty). */
export const packageRowsHaveDims = (rows) => {
  const list = toArrayIfObject(rows);
  return list.some((row) => {
    if (!row || typeof row !== "object" || Array.isArray(row)) {
      return false;
    }
    const dimKeys = ["weight", "Weight", "cf_weight", "length", "Length", "cf_length", "width", "Width", "height", "Height"];
    return dimKeys.some((key) => {
      const raw = row[key];
      if (raw == null || raw === "") return false;
      if (typeof raw === "object" && !Array.isArray(raw)) {
        const v = raw.value ?? raw.id ?? raw.label;
        return v != null && String(v).trim() !== "";
      }
      return String(raw).trim() !== "";
    });
  });
};

const isPagesPackageContextKey = (key) => {
  const lower = String(key || "").toLowerCase();
  return (
    lower === "package" ||
    lower === "packages" ||
    lower === "package_out" ||
    lower === "packageout"
  );
};

const mergeNonEmptyArrayField = (target, key, incoming) => {
  const incomingRows = toArrayIfObject(incoming);
  if (incomingRows.length === 0) {
    return;
  }
  const existingRows = toArrayIfObject(target[key]);
  if (existingRows.length === 0) {
    target[key] = incomingRows;
    return;
  }
  // Never replace rich SBR Package rows with equal-length slim Get Rates display rows.
  if (
    isPagesPackageContextKey(key) &&
    packageRowsHaveSubformLookupOrDescription(existingRows) &&
    !packageRowsHaveSubformLookupOrDescription(incomingRows)
  ) {
    return;
  }
  if (incomingRows.length >= existingRows.length) {
    target[key] = incomingRows;
  }
};

const GET_RATES_CONTEXT_ARRAY_KEYS = new Set([
  "shipObj",
  "ship_obj",
  "shipment_charges",
  "Shipment_charges",
  "Package",
  "package",
  "Package_out",
  "package_out",
  "from_additional_services",
  "to_additional_services",
]);

/** Flat keys where later context layers (e.g. search-by-reference) must override onload defaults. */
const GET_RATES_PACKAGE_TYPE_FLAT_KEYS = new Set([
  "package_type",
  "Package / Pallet",
  "package_pallet",
]);

const inferPackageTypeFromPackageRows = (ctx = {}) => {
  const rows = toArrayIfObject(ctx.Package ?? ctx.package ?? ctx.packages);
  if (rows.length === 0) {
    return null;
  }
  let palletCount = 0;
  let envelopeCount = 0;
  rows.forEach((row) => {
    if (!row || typeof row !== "object") {
      return;
    }
    const normalized = normalizePicklistPackageType(
      row.cf_pallet_type ??
        row.package_type ??
        row.Package_Type ??
        row["Package / Pallet"]
    );
    const lower = String(normalized || "").toLowerCase();
    if (lower === "pallet") {
      palletCount += 1;
    } else if (lower === "envelope") {
      envelopeCount += 1;
    }
  });
  if (palletCount > 0 && palletCount === rows.length) {
    return "Pallet";
  }
  if (envelopeCount > 0 && envelopeCount === rows.length) {
    return "Envelope";
  }
  return null;
};

const mergeGetRatesFlatContextFields = (target, source) => {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return;
  }
  Object.keys(source).forEach((key) => {
    if (GET_RATES_CONTEXT_ARRAY_KEYS.has(key)) {
      return;
    }
    const incoming = source[key];
    if (incoming == null || incoming === "") {
      return;
    }
    if (GET_RATES_PACKAGE_TYPE_FLAT_KEYS.has(key)) {
      const normalizedIncoming = normalizePicklistPackageType(incoming);
      if (normalizedIncoming != null && normalizedIncoming !== "") {
        target[key] = normalizedIncoming;
        if (key !== "package_type") {
          target.package_type = normalizedIncoming;
        } else {
          target["Package / Pallet"] = normalizedIncoming;
        }
      }
      return;
    }
    if (GET_RATES_SHIPMENT_ADDRESS_OVERWRITE_FLAT_KEYS.has(key)) {
      target[key] = incoming;
      return;
    }
    const existing = target[key];
    if (existing == null || existing === "") {
      target[key] = incoming;
    }
  });
};

/** Merge one execute-context layer: prefer rich Package / non-empty additional services. */
export const mergePagesExecuteContextLayer = (target, layer = {}) => {
  if (!layer || typeof layer !== "object" || Array.isArray(layer)) {
    return;
  }
  mergeGetRatesFlatContextFields(target, layer);

  if (layer.shipObj != null || layer.ship_obj != null) {
    const mergedShip = mergeShipObjArraysPreferRicher(
      target.shipObj ?? target.ship_obj,
      layer.shipObj ?? layer.ship_obj
    );
    if (mergedShip.length > 0) {
      target.shipObj = mergedShip;
      target.ship_obj = mergedShip;
    }
  }

  mergeNonEmptyArrayField(target, "shipment_charges", layer.shipment_charges);
  mergeNonEmptyArrayField(target, "Shipment_charges", layer.Shipment_charges);
  ["Package", "package", "Package_out", "package_out"].forEach((key) => {
    mergeNonEmptyArrayField(target, key, layer[key]);
  });
  mergeNonEmptyArrayField(
    target,
    "from_additional_services",
    layer.from_additional_services
  );
  mergeNonEmptyArrayField(
    target,
    "to_additional_services",
    layer.to_additional_services
  );
  mergeNonEmptyArrayField(
    target,
    "Ship From Additional Services",
    layer["Ship From Additional Services"]
  );
  mergeNonEmptyArrayField(
    target,
    "Ship To Additional Services",
    layer["Ship To Additional Services"]
  );
};

const mergeGetRatesContextLayer = mergePagesExecuteContextLayer;

/**
 * pageData keys for rates-results pages (prior Get_rates_config snapshots).
 * Must be stripped/excluded before a new Add → Get Rates redirect merge.
 */
export const collectRatesResultsPageDataKeys = (pageFlows = {}) => {
  const keys = new Set();
  const pageData = pageFlows?.pageData || {};
  Object.keys(pageData).forEach((k) => {
    const cfg = pageData[k];
    if (!isRatesResultsConfig(k, cfg)) {
      return;
    }
    keys.add(String(k));
    if (cfg?.id) {
      keys.add(String(cfg.id));
    }
    if (cfg?.configuration_api_name) {
      keys.add(String(cfg.configuration_api_name));
    }
  });
  readRatesResultsRegistry().forEach((k) => {
    if (k != null && String(k).trim() !== "") {
      keys.add(String(k));
    }
  });
  return [...keys];
};

/**
 * Merge params/response from loaded Pages configs.
 * Order: history (host first), then remaining keys; later non-empty layers win.
 * Skip rates-results keys — a prior Get_rates_config visit must not seed the next run.
 */
export const collectGetRatesContextFromPageFlows = (pageFlows) => {
  clearGetRatesSessionCache();
  const merged = {};
  const pageData = pageFlows?.pageData;
  if (!pageData || typeof pageData !== "object") {
    return merged;
  }
  const historyKeys = Array.isArray(pageFlows?.history)
    ? pageFlows.history.filter((k) => pageData[k])
    : [];
  const orderedKeys = [
    ...historyKeys,
    ...Object.keys(pageData).filter((k) => !historyKeys.includes(k)),
  ];
  orderedKeys.forEach((key) => {
    const cfg = pageData[key];
    // Prior /pages/Get_rates_config response must not overwrite Add_shipment host context.
    if (isRatesResultsConfig(key, cfg)) {
      return;
    }
    const pages = cfg?.pages;
    if (!pages || typeof pages !== "object") {
      return;
    }
    mergeGetRatesContextLayer(merged, omitEphemeralPagesParams(pages.params));
    mergeGetRatesContextLayer(merged, omitEphemeralPagesParams(pages.response));
  });
  return merged;
};

const parseAccidentalJsonString = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith("[") && !trimmed.startsWith("{")) return value;
  try {
    return JSON.parse(trimmed);
  } catch (e) {
    return value;
  }
};

const DEFAULT_LBS_UOM_ID = "93180943-8c1f-4756-9c63-ca7c50264180";
const LBS_UOM_LABEL = "LBS";

const resolveUomLabel = (uom) => {
  if (uom == null || uom === "") {
    return LBS_UOM_LABEL;
  }
  if (typeof uom === "object") {
    return uom.name ?? uom.value ?? uom.label ?? LBS_UOM_LABEL;
  }
  const text = String(uom);
  if (text === DEFAULT_LBS_UOM_ID || /^[0-9a-f-]{36}$/i.test(text)) {
    return LBS_UOM_LABEL;
  }
  return text;
};

/** Get Rates page markup expects PascalCase (pallet.Weight, pallet.UOM, …). */
export const toGetRatesPackageDisplayRow = (row) => {
  if (row == null || typeof row !== "object") {
    return null;
  }
  const weightRaw = row.weight ?? row.Weight ?? row.cf_weight;
  const weightNum = Number(weightRaw);
  if (!Number.isFinite(weightNum)) {
    return null;
  }
  const toFixed2 = (val) => {
    const n = Number(val);
    return Number.isFinite(n) ? n.toFixed(2) : val;
  };
  const length = toFixed2(row.length ?? row.Length ?? row.cf_length);
  const width = toFixed2(row.width ?? row.Width ?? row.cf_width);
  const height = toFixed2(row.height ?? row.Height ?? row.cf_height);
  const weight = toFixed2(weightRaw);
  const uomLabel = resolveUomLabel(row.uom ?? row.UOM ?? row.cf_unit_of_measure);
  return {
    weight,
    length,
    width,
    height,
    uom: row.uom ?? uomLabel,
    Weight: weight,
    Length: length,
    Width: width,
    Height: height,
    UOM: uomLabel,
  };
};

const mapPackagesForRatesDisplay = (packages) => {
  const normalized = normalizePagesHyperthreadPackages(packages);
  if (!Array.isArray(normalized)) {
    return normalized;
  }
  return normalized.map(toGetRatesPackageDisplayRow).filter(Boolean);
};

/** Align Pages Get Rates package payload with layout /rates/get-rates shape for HyperThread. */
export const normalizePagesHyperthreadPackages = (packages) => {
  if (!Array.isArray(packages)) {
    return packages;
  }
  return packages
    .map((row) => {
      if (row == null || typeof row !== "object") {
        return null;
      }
      if (Object.keys(row).length === 0) {
        return null;
      }
      const weightRaw = row.weight ?? row.Weight ?? row.cf_weight;
      const weightNum = Number(weightRaw);
      if (!Number.isFinite(weightNum)) {
        return null;
      }
      const toFixed2 = (val) => {
        const n = Number(val);
        return Number.isFinite(n) ? n.toFixed(2) : val;
      };
      let uom = row.uom ?? row.UOM ?? row.cf_unit_of_measure;
      if (uom != null && typeof uom === "object") {
        uom = uom.value ?? uom.name ?? DEFAULT_LBS_UOM_ID;
      }
      if (uom == null || uom === "") {
        uom = DEFAULT_LBS_UOM_ID;
      }
      return {
        weight: toFixed2(weightRaw),
        length: toFixed2(row.length ?? row.Length ?? row.cf_length),
        width: toFixed2(row.width ?? row.Width ?? row.cf_width),
        height: toFixed2(row.height ?? row.Height ?? row.cf_height),
        uom,
      };
    })
    .filter(Boolean);
};

const PAGES_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True when a row is a real Additional Service lookup entry (not a leaked step variable id). */
export const looksLikePagesAdditionalServiceRow = (row) => {
  if (row == null) {
    return false;
  }
  if (typeof row === "string") {
    const trimmed = row.trim();
    if (trimmed === "" || PAGES_UUID_RE.test(trimmed)) {
      return false;
    }
    return /^\d{8,}$/.test(trimmed);
  }
  if (typeof row !== "object" || Array.isArray(row)) {
    return false;
  }
  const display =
    row.name ?? row.Name ?? row.label ?? row.text ?? row.service_name;
  if (display != null && String(display).trim() !== "") {
    return true;
  }
  if (row.code != null && String(row.code).trim() !== "") {
    return true;
  }
  const recordId = row.record_id ?? row.recordId;
  if (recordId != null && /^\d{8,}$/.test(String(recordId))) {
    return true;
  }
  const lookupValue = row.value ?? row.id;
  if (lookupValue == null || lookupValue === "") {
    return false;
  }
  const scalar = String(lookupValue).trim();
  if (PAGES_UUID_RE.test(scalar)) {
    return false;
  }
  return /^\d{8,}$/.test(scalar);
};

/** Drop unresolved step variable ids and other non-service rows from MultiLookup arrays. */
export const sanitizePagesAdditionalServices = (services) => {
  if (services == null || services === "") {
    return [];
  }
  let parsed = services;
  if (typeof parsed === "string") {
    const trimmed = parsed.trim();
    if (PAGES_UUID_RE.test(trimmed)) {
      return [];
    }
    parsed = parsePagesResponseJsonIfNeeded(parsed);
  }
  if (!Array.isArray(parsed)) {
    return looksLikePagesAdditionalServiceRow(parsed) ? [parsed] : [];
  }
  return parsed.filter(looksLikePagesAdditionalServiceRow);
};

/** Day & Ross / LP hyper conditions match on service code (e.g. TLGDEL), not CRM lookup id. */
export const normalizePagesAdditionalServices = (services) => {
  const rows = sanitizePagesAdditionalServices(services);
  if (!Array.isArray(rows)) {
    return rows;
  }
  return rows.map((entry) => {
    if (entry == null || typeof entry !== "object") {
      return entry;
    }
    const label = String(entry.label ?? entry.name ?? "").toLowerCase();
    if (
      (label.includes("tailgate") || label.includes("tail gate")) &&
      !entry.code
    ) {
      return { ...entry, code: "TLGDEL" };
    }
    return entry;
  });
};

const parsePagesResponseJsonIfNeeded = (val) => {
  if (typeof val !== "string") {
    return val;
  }
  const trimmed = val.trim();
  if (
    trimmed === "" ||
    (trimmed[0] !== "[" && trimmed[0] !== "{" && trimmed[0] !== '"')
  ) {
    return val;
  }
  try {
    return JSON.parse(trimmed);
  } catch (e) {
    return val;
  }
};

const getPagesExecuteMappingList = (updatePayload) => {
  if (Array.isArray(updatePayload)) {
    return updatePayload;
  }
  if (updatePayload?.mappings && Array.isArray(updatePayload.mappings)) {
    return updatePayload.mappings;
  }
  return [];
};

const PAGES_ISO_COUNTRY_DISPLAY = {
  CA: "CANADA",
  US: "UNITED STATES",
};

const scalarPagesExecuteValue = (val) => {
  if (val == null || val === "") {
    return null;
  }
  if (typeof val === "boolean") {
    return val ? "true" : null;
  }
  let parsed = parseAccidentalJsonString(val);
  // Pages COUNTRY picklist / lookup-shaped: [{id:"CA"}] or {id:"CA"} — Layout uses .id.
  if (Array.isArray(parsed) && parsed.length > 0) {
    parsed = parsed[0];
  }
  if (parsed != null && typeof parsed === "object" && !Array.isArray(parsed)) {
    if ("selected_value" in parsed) {
      parsed = parsed.selected_value;
      if (Array.isArray(parsed) && parsed.length > 0) {
        parsed = parsed[0];
      }
    }
    if (parsed != null && typeof parsed === "object" && !Array.isArray(parsed)) {
      return (
        parsed.id ??
        parsed.name ??
        parsed.label ??
        parsed.value ??
        parsed.text ??
        null
      );
    }
  }
  const text = String(parsed).trim();
  return text === "" ? null : text;
};

/** Package / Envelope / Pallet picklist — reject boolean and string "false"/"true". */
export const normalizePicklistPackageType = (val) => {
  if (val === false || val === true) {
    return null;
  }
  const scalar = scalarPagesExecuteValue(val);
  if (scalar == null) {
    return null;
  }
  const lower = String(scalar).trim().toLowerCase();
  if (lower === "false" || lower === "true") {
    return null;
  }
  return scalar;
};

const formatPagesCountryDisplay = (val) => {
  const scalar = scalarPagesExecuteValue(val);
  if (scalar == null) {
    return null;
  }
  const upper = scalar.toUpperCase();
  if (upper.length === 2 && PAGES_ISO_COUNTRY_DISPLAY[upper]) {
    return PAGES_ISO_COUNTRY_DISPLAY[upper];
  }
  return scalar;
};

const isLikelyCountryCodeNotPostal = (val) =>
  val != null && /^[A-Za-z]{2}$/.test(String(val).trim());

const PAGES_EXECUTE_TO_SHIPOBJ_SCALAR = {
  package_type: "package_type",
  "Package / Pallet": "package_type",
  package_pallet: "package_type",
};

const PAGES_EXECUTE_TO_SHIPOBJ_ADDRESS = {
  to_postal_code: "to_postal_code",
  from_postal_code: "from_postal_code",
  to_postalcode: "to_postal_code",
  from_postalcode: "from_postal_code",
  to_city: "to_city",
  from_city: "from_city",
  to_company_name: "to_company_name",
  from_company_name: "from_company_name",
  to_address_1: "to_address_1",
  from_address_1: "from_address_1",
  to_address1: "to_address_1",
  from_address1: "from_address_1",
  to_address_2: "to_address_2",
  from_address_2: "from_address_2",
  to_address2: "to_address_2",
  from_address2: "from_address_2",
  to_province_state: "to_province_state",
  from_province_state: "from_province_state",
  to_province: "to_province_state",
  from_province: "from_province_state",
};

const PAGES_EXECUTE_COUNTRY_TO_SHIPOBJ = {
  To_country: "to_country",
  From_country: "from_country",
  to_country: "to_country",
  from_country: "from_country",
};

/** Shipment address scalars — SBR / same-page execute must replace account lookup defaults. */
const GET_RATES_SHIPMENT_ADDRESS_OVERWRITE_FLAT_KEYS = new Set([
  ...Object.keys(PAGES_EXECUTE_TO_SHIPOBJ_ADDRESS),
  ...Object.keys(PAGES_EXECUTE_COUNTRY_TO_SHIPOBJ),
  "from_company_name",
  "to_company_name",
  "from_phone",
  "to_phone",
  "from_email",
  "to_email",
  "from_attention",
  "to_attention",
  "from_instruction",
  "to_instruction",
  "from_address",
  "to_address",
  "from_address_lookup",
  "to_address_lookup",
  "from_residential",
  "to_residential",
  "is_from_residential",
  "is_to_residential",
  "is_notify_shipper",
  "is_notify_consignee",
  // SBR / same-page must overwrite prior order reference (not only when empty).
  "reference",
]);

/** Layout Get Rates sidebar: company, city, postal, country — no street lines. */
const applyLayoutRatesSidebarAddressParity = (row) => {
  if (!row || typeof row !== "object" || Array.isArray(row)) {
    return row;
  }
  const next = { ...row };
  ["from_address_1", "from_address_2", "to_address_1", "to_address_2"].forEach(
    (key) => {
      next[key] = null;
    }
  );
  return next;
};

const SHIPOBJ_MARKUP_FIELDS = [
  "to_company_name",
  "from_company_name",
  "to_city",
  "from_city",
  "to_postal_code",
  "from_postal_code",
  "to_country",
  "from_country",
  "to_province_state",
  "from_province_state",
];

/** Hyper / pages may use PascalCase country keys; markup uses snake_case on data.* */
const canonicalizeShipObjRowFieldNames = (row) => {
  if (!row || typeof row !== "object" || Array.isArray(row)) {
    return row;
  }
  const next = { ...row };
  const aliasPairs = [
    ["To_country", "to_country"],
    ["From_country", "from_country"],
    ["to_country", "to_country"],
    ["from_country", "from_country"],
    ["To_postal_code", "to_postal_code"],
    ["From_postal_code", "from_postal_code"],
    ["To_city", "to_city"],
    ["From_city", "from_city"],
    ["To_company_name", "to_company_name"],
    ["From_company_name", "from_company_name"],
  ];
  aliasPairs.forEach(([fromKey, toKey]) => {
    if (
      (next[toKey] == null || next[toKey] === "") &&
      next[fromKey] != null &&
      next[fromKey] !== ""
    ) {
      next[toKey] = next[fromKey];
    }
  });
  return next;
};

const rowHasShipObjMarkupAddress = (row) => {
  const normalized = canonicalizeShipObjRowFieldNames(row);
  if (!normalized || typeof normalized !== "object") {
    return false;
  }
  return SHIPOBJ_MARKUP_FIELDS.some((key) => {
    const val = normalized[key];
    return val != null && String(val).trim() !== "";
  });
};

const mergeShipObjRowPreferPrimary = (fallbackRow, primaryRow) => {
  const merged = { ...(fallbackRow || {}) };
  const primary = canonicalizeShipObjRowFieldNames(primaryRow) || {};
  Object.keys(primary).forEach((key) => {
    const val = primary[key];
    if (val != null && val !== "") {
      merged[key] = val;
    }
  });
  return merged;
};

const normalizePagesShipObjAddressRow = (row) => {
  if (!row || typeof row !== "object" || Array.isArray(row)) {
    return row;
  }
  const next = canonicalizeShipObjRowFieldNames(row);

  if (!next.to_postal_code || isLikelyCountryCodeNotPostal(next.to_postal_code)) {
    const alt =
      next.to_postalcode ??
      next["to_postal_/_zip_code"];
    if (alt && !isLikelyCountryCodeNotPostal(alt)) {
      next.to_postal_code = alt;
    }
  }
  if (
    !next.from_postal_code ||
    isLikelyCountryCodeNotPostal(next.from_postal_code)
  ) {
    const alt =
      next.from_postalcode ??
      next["from_postal_/_zip_code"];
    if (alt && !isLikelyCountryCodeNotPostal(alt)) {
      next.from_postal_code = alt;
    }
  }

  if (next.to_country != null) {
    next.to_country =
      formatPagesCountryDisplay(next.to_country) ?? next.to_country;
  }
  if (next.from_country != null) {
    next.from_country =
      formatPagesCountryDisplay(next.from_country) ?? next.from_country;
  }

  return next;
};

const buildPagesExecuteShipObjAddressPatch = (executeMappings) => {
  const patch = {};
  getPagesExecuteMappingList(executeMappings).forEach((mapping) => {
    const name = mapping?.pages_variable_name;
    const val = mapping?.selected_value;
    if (!name || val == null || val === "") {
      return;
    }
    const shipKey =
      PAGES_EXECUTE_TO_SHIPOBJ_ADDRESS[name] ||
      PAGES_EXECUTE_COUNTRY_TO_SHIPOBJ[name] ||
      PAGES_EXECUTE_TO_SHIPOBJ_SCALAR[name];
    if (!shipKey) {
      return;
    }
    if (shipKey === "package_type") {
      const scalar = normalizePicklistPackageType(val);
      if (scalar) {
        patch.package_type = scalar;
      }
      return;
    }
    if (shipKey.endsWith("country")) {
      const formatted = formatPagesCountryDisplay(val);
      if (formatted) {
        patch[shipKey] = formatted;
      }
      return;
    }
    const scalar = scalarPagesExecuteValue(val);
    if (scalar) {
      patch[shipKey] = scalar;
    }
  });
  return patch;
};

const mergePagesExecuteIntoShipObj = (outputs, executeMappings) => {
  const next = { ...outputs };
  const patch = buildPagesExecuteShipObjAddressPatch(executeMappings);
  const shipKeys = ["shipObj", "ship_obj"];
  let shipArr = null;
  let shipKeyUsed = null;

  shipKeys.forEach((key) => {
    if (shipArr != null || next[key] == null) {
      return;
    }
    let raw = next[key];
    if (typeof raw === "string") {
      raw = parsePagesResponseJsonIfNeeded(raw);
    }
    if (Array.isArray(raw) && raw.length > 0) {
      shipArr = raw.map((row) => ({ ...row }));
      shipKeyUsed = key;
    } else if (raw != null && typeof raw === "object") {
      shipArr = [{ ...raw }];
      shipKeyUsed = key;
    }
  });

  if (shipArr == null) {
    if (Object.keys(patch).length === 0) {
      return next;
    }
    shipArr = [{}];
    shipKeyUsed = "shipObj";
  }

  if (Object.keys(patch).length > 0) {
    shipArr = shipArr.map((row, index) => {
      if (index !== 0) {
        return normalizePagesShipObjAddressRow(row);
      }
      // Execute / form address always replaces stale shipObj (prior Get Rates
      // session or account-lookup defaults). Only keep an existing postal when
      // the patch would overwrite a real postal with a 2-letter country code.
      const mergedRow = { ...row };
      Object.keys(patch).forEach((field) => {
        const existing = row[field];
        const incoming = patch[field];
        if (
          field.endsWith("postal_code") &&
          existing &&
          !isLikelyCountryCodeNotPostal(existing) &&
          isLikelyCountryCodeNotPostal(incoming)
        ) {
          mergedRow[field] = existing;
        } else if (field.endsWith("country") && incoming) {
          mergedRow[field] =
            formatPagesCountryDisplay(incoming) || incoming;
        } else if (incoming != null && incoming !== "") {
          mergedRow[field] = incoming;
        }
      });
      return normalizePagesShipObjAddressRow(mergedRow);
    });
  } else {
    shipArr = shipArr.map(normalizePagesShipObjAddressRow);
  }

  next[shipKeyUsed] = shipArr;

  const primary = shipArr[0];
  // Mirror onto flat keys so ensureGetRatesMarkupOutputResponse session merge
  // + ensureShipObjWithAddressFromContext do not rebuild from a prior MOTION/
  // Beamsville rates snapshot while shipObj already has the current shipment.
  if (primary && typeof primary === "object") {
    SHIPOBJ_MARKUP_FIELDS.forEach((key) => {
      const val = primary[key];
      if (val != null && String(val).trim() !== "") {
        next[key] = val;
      }
    });
    if (primary.package_type != null && primary.package_type !== "") {
      next.package_type = primary.package_type;
      next["Package / Pallet"] = primary.package_type;
    }
  }

  return next;
};

const unwrapHyperthreadVariablePayload = (raw) => {
  if (raw == null) {
    return raw;
  }
  if (typeof raw !== "object" || Array.isArray(raw)) {
    return raw;
  }
  if ("selected_value" in raw) {
    return unwrapHyperthreadVariablePayload(raw.selected_value);
  }
  if ("value" in raw) {
    const inner = raw.value;
    if (inner != null && typeof inner === "object") {
      return inner;
    }
    return inner;
  }
  return raw;
};

const looksLikeShipmentChargeRow = (row) => {
  if (!row || typeof row !== "object") {
    return false;
  }
  return (
    row.final_rate != null ||
    row.customer_rate != null ||
    row.finalRate != null ||
    row.service_name != null ||
    row.serviceName != null ||
    row["Final Rate"] != null
  );
};

const hyperthreadVariableToChargeRows = (variable) => {
  if (!variable) {
    return [];
  }
  let parsed = unwrapHyperthreadVariablePayload(
    variable.selected_value ??
      variable.selectedValue ??
      variable.value ??
      variable
  );
  if (typeof parsed === "string") {
    parsed = parsePagesResponseJsonIfNeeded(parsed);
  }
  parsed = unwrapHyperthreadVariablePayload(parsed);
  if (Array.isArray(parsed)) {
    return parsed.filter(looksLikeShipmentChargeRow);
  }
  if (parsed && typeof parsed === "object") {
    if (looksLikeShipmentChargeRow(parsed)) {
      return [parsed];
    }
    const nested =
      parsed?.rate_info?.output?.["shipment charges"] ??
      parsed?.rate_info?.output?.shipment_charges ??
      parsed?.output?.["shipment charges"];
    if (Array.isArray(nested)) {
      return nested.filter(looksLikeShipmentChargeRow);
    }
  }
  return [];
};

const shipmentChargeRowKey = (row) => {
  const service = String(
    row?.service_name ?? row?.serviceName ?? row?.Service_name ?? ""
  );
  const carrier = String(row?.carrier_name ?? row?.carrierName ?? "");
  const rate = String(
    row?.final_rate ?? row?.finalRate ?? row?.["Final Rate"] ?? ""
  );
  return `${carrier}::${service}::${rate}`;
};

/** Merge all hyperthread globals named shipment_charges (Day & Ross, LP, etc.). */
const hyperthreadVariableToDataRows = (variable) => {
  if (!variable) {
    return [];
  }
  let parsed =
    variable.selected_value ??
    variable.selectedValue ??
    variable.value ??
    variable.Value ??
    variable;
  parsed = unwrapHyperthreadVariablePayload(parsed);
  if (typeof parsed === "string") {
    parsed = parsePagesResponseJsonIfNeeded(parsed);
  }
  parsed = unwrapHyperthreadVariablePayload(parsed);
  if (Array.isArray(parsed)) {
    return parsed.filter(
      (row) =>
        row != null &&
        typeof row === "object" &&
        !Array.isArray(row) &&
        Object.keys(row).length > 0
    );
  }
  if (parsed && typeof parsed === "object") {
    return [parsed];
  }
  return [];
};

const findHyperthreadVariableByApi = (variables = [], ...candidates) => {
  const wanted = candidates.map((c) =>
    String(c).toLowerCase().replace(/\s+/g, "_")
  );
  return (variables || []).find((variable) => {
    const api = String(variable?.api_name ?? variable?.apiName ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");
    const varName = String(
      variable?.variableName ?? variable?.variable_name ?? ""
    )
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");
    return wanted.includes(api) || wanted.includes(varName);
  });
};

const mapAdditionalServicesForRatesDisplay = (services) => {
  if (!Array.isArray(services)) {
    return services;
  }
  return normalizePagesAdditionalServices(services).map((entry) => {
    if (entry == null || typeof entry !== "object") {
      return entry;
    }
    const displayName =
      entry.Name ??
      entry.name ??
      entry.label ??
      entry.text ??
      "";
    return { ...entry, Name: displayName, name: displayName };
  });
};

export const mergeHyperthreadShipmentChargesFromVariables = (variables = []) => {
  const merged = [];
  const seen = new Set();
  (variables || []).forEach((variable) => {
    const apiName = String(variable?.api_name ?? variable?.apiName ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");
    if (apiName !== "shipment_charges") {
      return;
    }
    hyperthreadVariableToChargeRows(variable).forEach((row) => {
      const key = shipmentChargeRowKey(row);
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      merged.push(row);
    });
  });
  return merged;
};

const pickChargeField = (row, keys) => {
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    if (row[k] != null && row[k] !== "") {
      return row[k];
    }
    const normalizedKey = k.replace(/[\s_-]+/g, "").toLowerCase();
    const match = Object.keys(row).find(
      (rk) => rk.replace(/[\s_-]+/g, "").toLowerCase() === normalizedKey
    );
    if (match && row[match] != null && row[match] !== "") {
      return row[match];
    }
  }
  return undefined;
};

const unwrapImageOrScalar = (val) => {
  if (val == null) {
    return val;
  }
  if (typeof val === "object") {
    return val.value ?? val.id ?? val.name ?? val.label ?? "";
  }
  return val;
};

/** Align hyperthread charge rows with Get Rates markup (layout field names). */
export const normalizePagesShipmentChargeDisplayRow = (row) => {
  if (!row || typeof row !== "object") {
    return row;
  }
  const final_rate = pickChargeField(row, ["final_rate", "finalRate", "Final Rate"]);
  const customer_rate = pickChargeField(row, [
    "customer_rate",
    "customerRate",
    "Customer Rate",
  ]);
  const service_name = pickChargeField(row, [
    "service_name",
    "serviceName",
    "Service Name",
    "Service_name",
  ]);
  const carrier_name = pickChargeField(row, [
    "carrier_name",
    "carrierName",
    "Carrier Name",
  ]);
  const imageid = unwrapImageOrScalar(
    pickChargeField(row, [
      "imageid",
      "imageId",
      "carrier_image",
      "carrierImage",
      "Carrier Image",
    ])
  );
  const chargeable_weight = pickChargeField(row, [
    "chargeable_weight",
    "chargeable_wt",
    "chargeable weight",
    "Chargeable WT",
  ]);
  const minimum_transit_days = pickChargeField(row, [
    "minimum_transit_days",
    "min_transit_days",
    "estimated_transit_days",
    "transit_days",
  ]);
  const maximum_transit_days = pickChargeField(row, [
    "maximum_transit_days",
    "max_transit_days",
  ]);
  const currency = pickChargeField(row, ["currency", "Currency"]) ?? "CAD";
  const carrier_image =
    imageid ?? pickChargeField(row, ["carrier_image", "carrierImage"]);
  return {
    ...row,
    final_rate,
    customer_rate,
    service_name,
    carrier_name,
    imageid,
    carrier_image,
    chargeable_weight,
    minimum_transit_days,
    maximum_transit_days,
    currency,
  };
};

/**
 * Get Rates page HTML renders rate cards twice when both shipment_charges and
 * get_rate_with_shipments are populated (see Get_rates_config markup). Layout
 * uses a single source; keep only one for Pages display.
 */
const applyGetRatesChargeDisplayExclusiveSource = (next = {}) => {
  if (!next || typeof next !== "object") {
    return next;
  }
  let charges = next.shipment_charges ?? next.Shipment_charges;
  if (typeof charges === "string") {
    charges = parsePagesResponseJsonIfNeeded(charges);
  }
  if (charges != null && typeof charges === "object" && !Array.isArray(charges)) {
    charges = [charges];
  }
  const rows = dedupeAndNormalizeShipmentCharges(
    Array.isArray(charges) ? charges : []
  );
  if (rows.length > 0) {
    next.shipment_charges = rows;
    next.Shipment_charges = rows;
    next.get_rate_with_shipments = [];
    return next;
  }
  const legacy = next.get_rate_with_shipments;
  if (Array.isArray(legacy) && legacy.length > 0) {
    return next;
  }
  next.get_rate_with_shipments = [];
  return next;
};

export const buildPagesRateWithShipmentsFromCharges = (charges = []) => {
  if (!Array.isArray(charges) || charges.length === 0) {
    return [];
  }
  const byCarrier = {};
  charges.forEach((rawRow) => {
    const row = normalizePagesShipmentChargeDisplayRow(rawRow);
    const carrierKey = String(row?.carrier_name ?? "rates");
    if (!byCarrier[carrierKey]) {
      byCarrier[carrierKey] = [];
    }
    byCarrier[carrierKey].push(row);
  });
  return Object.keys(byCarrier).map((carrierKey) => ({
    rate_info: {
      output: {
        "shipment charges": byCarrier[carrierKey],
      },
    },
  }));
};

const dedupeAndNormalizeShipmentCharges = (rows = []) => {
  const seen = new Set();
  const out = [];
  (rows || []).forEach((row) => {
    const norm = normalizePagesShipmentChargeDisplayRow(row);
    const key = shipmentChargeRowKey(norm);
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    out.push(norm);
  });
  return out;
};

export const enrichPagesGetRatesOutputsWithHyperthreadVariables = (
  outputs = {},
  variables = []
) => {
  if (!variables?.length) {
    return outputs;
  }
  const next = { ...outputs };

  const shipVar = findHyperthreadVariableByApi(variables, "ship_obj", "shipobj");
  const hyperShipRows = hyperthreadVariableToDataRows(shipVar)
    .map(normalizePagesShipObjAddressRow)
    .map(applyLayoutRatesSidebarAddressParity);
  if (hyperShipRows.length > 0) {
    const mergedShip = mergeShipObjArraysPreferRicher(
      next.shipObj ?? next.ship_obj,
      hyperShipRows
    );
    if (mergedShip.length > 0) {
      next.shipObj = mergedShip;
      next.ship_obj = mergedShip;
      // Keep flat address keys in lockstep so ensureShipObjWithAddressFromContext
      // cannot rebuild the sidebar from a prior Get Rates session snapshot.
      const primary = canonicalizeShipObjRowFieldNames(mergedShip[0]);
      if (primary && typeof primary === "object") {
        SHIPOBJ_MARKUP_FIELDS.forEach((key) => {
          const val = primary[key];
          if (val != null && String(val).trim() !== "") {
            next[key] = val;
          }
        });
      }
    }
  }

  const packageKeys = ["Package", "package", "Package_out", "package_out"];
  const hasPackageRows = packageKeys.some((key) => {
    const rows = next[key];
    return Array.isArray(rows) && rows.length > 0;
  });
  if (!hasPackageRows) {
    const packagesVar = findHyperthreadVariableByApi(
      variables,
      "packages",
      "package",
      "Package",
      "Packages"
    );
    const packageRows = hyperthreadVariableToDataRows(packagesVar);
    if (packageRows.length > 0) {
      const displayRows = mapPackagesForRatesDisplay(packageRows);
      if (displayRows.length > 0) {
        next.Package = displayRows;
        next.Package_out = displayRows;
      }
    }
  }

  const fillAdditionalServices = (pagesKey, ...apiCandidates) => {
    const sanitizedExisting = sanitizePagesAdditionalServices(next[pagesKey]);
    if (sanitizedExisting.length > 0) {
      next[pagesKey] = mapAdditionalServicesForRatesDisplay(sanitizedExisting);
      return;
    }
    const variable = findHyperthreadVariableByApi(variables, ...apiCandidates);
    const rows = sanitizePagesAdditionalServices(
      hyperthreadVariableToDataRows(variable)
    );
    if (rows.length > 0) {
      next[pagesKey] = mapAdditionalServicesForRatesDisplay(rows);
    } else {
      next[pagesKey] = [];
    }
  };
  fillAdditionalServices(
    "from_additional_services",
    "From_add_service_new",
    "from_add_service_new",
    "ship_from_additiona_services",
    "from_additonal_services",
    "from_additional_services"
  );
  fillAdditionalServices(
    "to_additional_services",
    "To_add_service_new",
    "to_add_service_new",
    "ship_to_additional_service",
    "to_additonal_services",
    "to_additional_services"
  );

  const mergedFromGlobals = mergeHyperthreadShipmentChargesFromVariables(variables);
  let existing = next.shipment_charges ?? next.Shipment_charges;
  if (typeof existing === "string") {
    existing = parsePagesResponseJsonIfNeeded(existing);
  }
  const existingRows = Array.isArray(existing)
    ? existing
    : existing && typeof existing === "object"
    ? [existing]
    : [];
  const combined = dedupeAndNormalizeShipmentCharges([
    ...existingRows,
    ...mergedFromGlobals,
  ]);
  if (combined.length === 0) {
    return next;
  }
  next.shipment_charges = combined;
  if (next.Shipment_charges != null) {
    next.Shipment_charges = combined;
  }
  return applyGetRatesChargeDisplayExclusiveSource(next);
};

export const buildPagesRateWithShipmentsFromOutputResponse = (outputResponse = {}) => {
  if (Array.isArray(outputResponse?.get_rate_with_shipments)) {
    return { get_rate_with_shipments: outputResponse.get_rate_with_shipments };
  }
  let charges = outputResponse?.shipment_charges ?? outputResponse?.Shipment_charges;
  if (charges != null && typeof charges === "object" && !Array.isArray(charges)) {
    charges = unwrapHyperthreadVariablePayload(charges);
  }
  if (typeof charges === "string") {
    charges = parsePagesResponseJsonIfNeeded(charges);
  }
  if (!Array.isArray(charges)) {
    return { get_rate_with_shipments: [] };
  }
  return {
    get_rate_with_shipments: buildPagesRateWithShipmentsFromCharges(charges),
  };
};

/** Coerce hyper/pages execute outputs into shapes Get Rates markup can render. */
export const normalizePagesGetRatesRedirectOutputs = (outputs = {}, options = {}) => {
  let next = { ...(outputs || {}) };
  if (options?.hyperthreadVariables?.length) {
    next = enrichPagesGetRatesOutputsWithHyperthreadVariables(
      next,
      options.hyperthreadVariables
    );
  }

  Object.keys(next).forEach((key) => {
    next[key] = parsePagesResponseJsonIfNeeded(next[key]);
  });

  if (next.shipObj == null && next.ship_obj != null) {
    next.shipObj = next.ship_obj;
  }
  if (next.Package == null && next.Package_out != null) {
    next.Package = next.Package_out;
  }

  ["shipment_charges", "Shipment_charges"].forEach((key) => {
    if (next[key] == null) {
      return;
    }
    let charges = next[key];
    if (typeof charges === "string") {
      charges = parsePagesResponseJsonIfNeeded(charges);
    }
    if (charges != null && typeof charges === "object" && !Array.isArray(charges)) {
      charges = [charges];
    }
    if (Array.isArray(charges)) {
      next[key] = charges.map(normalizePagesShipmentChargeDisplayRow);
    }
  });

  ["shipObj", "ship_obj"].forEach((key) => {
    if (next[key] == null) {
      return;
    }
    let shipObj = next[key];
    if (typeof shipObj === "string") {
      shipObj = parsePagesResponseJsonIfNeeded(shipObj);
    }
    if (shipObj != null && typeof shipObj === "object" && !Array.isArray(shipObj)) {
      shipObj = [shipObj];
    }
    if (Array.isArray(shipObj)) {
      next[key] = shipObj
        .map(normalizePagesShipObjAddressRow)
        .map(applyLayoutRatesSidebarAddressParity);
    }
  });

  const hasRatesResult =
    next.shipment_charges != null || next.Shipment_charges != null;
  const hasShipObjContext =
    next.shipObj != null || next.ship_obj != null;
  const packageOutputKeys = ["Package", "package", "Package_out", "package_out"];
  const packageRowsHaveLookup = packageOutputKeys.some((key) => {
    const rows = next[key];
    return (
      Array.isArray(rows) &&
      rows.some(
        (row) =>
          row &&
          typeof row === "object" &&
          row.cf_packages != null &&
          typeof row.cf_packages === "object" &&
          !Array.isArray(row.cf_packages)
      )
    );
  });
  const stripPackagesForRatesDisplay =
    hasRatesResult && hasShipObjContext && !packageRowsHaveLookup;

  if (stripPackagesForRatesDisplay) {
    packageOutputKeys.forEach((key) => {
      if (Array.isArray(next[key]) && next[key].length > 0) {
        const mapped = mapPackagesForRatesDisplay(next[key]);
        if (mapped.length > 0) {
          next[key] = mapped;
        }
      }
    });
  }

  const normalizedPackageType = normalizePicklistPackageType(next.package_type);
  if (normalizedPackageType != null && normalizedPackageType !== "") {
    next.package_type = normalizedPackageType;
    const packageType = normalizedPackageType;
    ["shipObj", "ship_obj"].forEach((key) => {
      if (!Array.isArray(next[key])) {
        return;
      }
      next[key] = next[key].map((row, index) =>
        index === 0 ? { ...row, package_type: packageType } : row
      );
    });
  } else if (
    next.package_type === false ||
    next.package_type === "false" ||
    next.package_type === true ||
    next.package_type === "true"
  ) {
    delete next.package_type;
    delete next["Package / Pallet"];
    ["shipObj", "ship_obj"].forEach((key) => {
      if (!Array.isArray(next[key])) {
        return;
      }
      next[key] = next[key].map((row) => {
        if (!row || typeof row !== "object") {
          return row;
        }
        const copy = { ...row };
        if (
          copy.package_type === false ||
          copy.package_type === "false" ||
          copy.package_type === true ||
          copy.package_type === "true"
        ) {
          delete copy.package_type;
        }
        return copy;
      });
    });
  }

  const mapAdditionalServicesForDisplay = (services) => {
    if (!Array.isArray(services)) {
      return services;
    }
    return normalizePagesAdditionalServices(services).map((entry) => {
      if (entry == null || typeof entry !== "object") {
        return entry;
      }
      const displayName =
        entry.Name ??
        entry.name ??
        entry.label ??
        entry.text ??
        "";
      return { ...entry, Name: displayName, name: displayName };
    });
  };

  ["to_additional_services", "from_additional_services"].forEach((key) => {
    if (next[key] != null) {
      const services = sanitizePagesAdditionalServices(next[key]);
      next[key] = mapAdditionalServicesForDisplay(services);
    }
  });

  if (Array.isArray(next.to_additional_services)) {
    next["Ship To Additional Services"] = next.to_additional_services;
  }
  if (Array.isArray(next.from_additional_services)) {
    next["Ship From Additional Services"] = next.from_additional_services;
  }

  const withShipmentDate = applyPagesLayoutShipmentDateParity(next);

  return applyGetRatesChargeDisplayExclusiveSource(withShipmentDate);
};

const buildShipObjRowFromPagesContext = (ctx = {}) => {
  const row = {};
  const assign = (pagesKey, shipKey) => {
    if (ctx[pagesKey] == null || ctx[pagesKey] === "") {
      return;
    }
    const scalar = scalarPagesExecuteValue(ctx[pagesKey]);
    if (scalar != null && scalar !== "") {
      if (row[shipKey] == null || row[shipKey] === "") {
        row[shipKey] = scalar;
      }
    }
  };
  Object.entries(PAGES_EXECUTE_TO_SHIPOBJ_ADDRESS).forEach(([pagesKey, shipKey]) => {
    assign(pagesKey, shipKey);
  });
  Object.entries(PAGES_EXECUTE_COUNTRY_TO_SHIPOBJ).forEach(([pagesKey, shipKey]) => {
    if (row[shipKey]) {
      return;
    }
    assign(pagesKey, shipKey);
  });
  Object.entries(PAGES_EXECUTE_TO_SHIPOBJ_SCALAR).forEach(([pagesKey, shipKey]) => {
    if (row[shipKey]) {
      return;
    }
    assign(pagesKey, shipKey);
  });
  if (Object.keys(row).length === 0) {
    return null;
  }
  return normalizePagesShipObjAddressRow(
    applyLayoutRatesSidebarAddressParity(row)
  );
};

const ensureShipObjWithAddressFromContext = (base = {}) => {
  // Flats are source of truth here (form / execute already mirrored onto flats
  // in mergePagesExecuteIntoShipObj + HT enrich). Prefer built-from-flats over
  // a stale session shipObj row when merging.
  const built = buildShipObjRowFromPagesContext(base);
  const keys = ["shipObj", "ship_obj"];
  let touched = false;
  keys.forEach((key) => {
    if (base[key] == null) {
      return;
    }
    let rows = base[key];
    if (typeof rows === "string") {
      rows = parsePagesResponseJsonIfNeeded(rows);
    }
    rows = unwrapHyperthreadVariablePayload(rows);
    if (rows != null && typeof rows === "object" && !Array.isArray(rows)) {
      rows = [rows];
    }
    if (!Array.isArray(rows)) {
      return;
    }
    if (rows.length === 0 && built) {
      base.shipObj = [built];
      touched = true;
      return;
    }
    base.shipObj = rows.map((row, index) => {
      const normalized = canonicalizeShipObjRowFieldNames(row);
      if (index !== 0) {
        return normalizePagesShipObjAddressRow(
          applyLayoutRatesSidebarAddressParity(normalized)
        );
      }
      const merged = built
        ? mergeShipObjRowPreferPrimary(normalized, built)
        : normalized;
      return normalizePagesShipObjAddressRow(
        applyLayoutRatesSidebarAddressParity(merged)
      );
    });
    touched = true;
  });
  if (!touched && built) {
    base.shipObj = [built];
  }
  if (base.shipObj != null && base.ship_obj == null) {
    base.ship_obj = base.shipObj;
  }
  return base;
};

/** Merge params + pages.response so Get Rates markup always receives shipObj, Package, additional services. */
export const ensureGetRatesMarkupOutputResponse = (merged = {}, options = {}) => {
  const formFlat = options.fieldFormPayload
    ? flattenFormPayloadForPagesExecute(options.fieldFormPayload)
    : {};
  clearGetRatesSessionCache();
  const flowContext = options.pageFlows
    ? collectGetRatesContextFromPageFlows(options.pageFlows)
    : {};
  const base = { ...flowContext };
  mergeGetRatesContextLayer(base, formFlat);
  mergeGetRatesContextLayer(base, merged || {});

  const flatPackageType = normalizePicklistPackageType(
    base["Package / Pallet"] ?? base.package_type
  );
  const inferredFromRows = inferPackageTypeFromPackageRows(base);
  const shipObjPackageType = normalizePicklistPackageType(
    (Array.isArray(base.shipObj) && base.shipObj[0]?.package_type) ||
      (Array.isArray(base.ship_obj) && base.ship_obj[0]?.package_type)
  );
  let scalarType = flatPackageType;
  if (
    inferredFromRows === "Pallet" &&
    (flatPackageType == null ||
      flatPackageType === "" ||
      flatPackageType === "Package")
  ) {
    scalarType = "Pallet";
  } else if (scalarType == null || scalarType === "") {
    scalarType = inferredFromRows || shipObjPackageType;
  }
  if (scalarType != null && scalarType !== "") {
    base["Package / Pallet"] = scalarType;
    base.package_type = scalarType;
  } else if (
    base["Package / Pallet"] === false ||
    base["Package / Pallet"] === "false" ||
    base.package_type === false ||
    base.package_type === "false"
  ) {
    delete base["Package / Pallet"];
    delete base.package_type;
  }

  ensureShipObjWithAddressFromContext(base);

  const packageKeys = [
    "Package",
    "package",
    "Package_out",
    "package_out",
    "Packages",
    "packages",
  ];
  const hasPackages = packageKeys.some(
    (key) => Array.isArray(base[key]) && base[key].length > 0
  );
  const resolvePackageRowsForRatesDisplay = () => {
    const keys = [
      "Package",
      "package",
      "Package_out",
      "package_out",
      "Packages",
      "packages",
    ];
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      let rows = base[key];
      if (rows == null) {
        continue;
      }
      if (typeof rows === "string") {
        rows = parsePagesResponseJsonIfNeeded(rows);
      }
      if (!Array.isArray(rows) || rows.length === 0) {
        continue;
      }
      const display = mapPackagesForRatesDisplay(rows);
      if (display.length > 0) {
        return display;
      }
      const normalized = normalizePagesHyperthreadPackages(rows);
      const retry = mapPackagesForRatesDisplay(normalized);
      if (retry.length > 0) {
        return retry;
      }
    }
    return null;
  };

  // Sidebar markup iterates Package_out (not Package). Form/HT may only fill Package;
  // mirror slim display rows onto Package_out only — never overwrite Package when it
  // already has rich SBR rows (cf_packages + description).
  {
    const resolvedPackages = resolvePackageRowsForRatesDisplay();
    const packageOutDisplay = Array.isArray(base.Package_out)
      ? mapPackagesForRatesDisplay(base.Package_out)
      : [];
    const packageOutNeedsFill =
      !Array.isArray(base.Package_out) ||
      base.Package_out.length === 0 ||
      packageOutDisplay.length === 0;
    const packageIsRich = packageRowsHaveSubformLookupOrDescription(
      base.Package ?? base.package
    );
    if (resolvedPackages && resolvedPackages.length > 0) {
      if (packageOutNeedsFill) {
        base.Package_out = resolvedPackages;
      }
      if (!hasPackages) {
        // Only seed Package from slim rates rows when nothing is present yet.
        base.Package = resolvedPackages;
        if (packageOutNeedsFill) {
          base.Package_out = resolvedPackages;
        }
      } else if (
        !packageIsRich &&
        packageOutNeedsFill === false &&
        resolvedPackages.length >
          (Array.isArray(base.Package_out) ? base.Package_out.length : 0)
      ) {
        base.Package_out = resolvedPackages;
      }
    }
  }

  [
    ["from_additional_services", "Ship From Additional Services"],
    ["to_additional_services", "Ship To Additional Services"],
  ].forEach(([key, altKey]) => {
    let existing = base[key];
    if (existing === "" || existing === "null") {
      existing = null;
      base[key] = null;
    }
    const empty =
      existing == null ||
      (Array.isArray(existing) && existing.length === 0);
    const alt = base[altKey];
    if (empty && Array.isArray(alt) && alt.length > 0) {
      base[key] = alt;
    }
    if (Array.isArray(base[key]) && base[key].length > 0) {
      base[altKey] = base[key];
    }
  });

  // shipment_details is the tracking result list. pageFlows keeps the previous
  // Tracking display response, and the flat merge keeps the first non-empty
  // array, so a new one-order search was still painted with the old rows.
  // This execute's shipment_details replaces that list. Get Rates keys are
  // left as merged above.
  if (
    merged &&
    Object.prototype.hasOwnProperty.call(merged, "shipment_details") &&
    merged.shipment_details != null &&
    merged.shipment_details !== ""
  ) {
    base.shipment_details = merged.shipment_details;
  }

  return normalizePagesGetRatesRedirectOutputs(base, options);
};

export const PAGES_GET_RATES_PASS_THROUGH_VARIABLES = [
  "to_additional_services",
  "from_additional_services",
  "package_type",
  "Package / Pallet",
];

const PAGES_PACKAGE_PASS_THROUGH_VARIABLE = "Package";

const parsePagesPackagePassThroughValue = (val) => {
  if (val == null || val === "") {
    return null;
  }
  let parsed = parseAccidentalJsonString(val);
  if (!Array.isArray(parsed)) {
    parsed = parsePagesResponseJsonIfNeeded(parsed);
  }
  if (!Array.isArray(parsed)) {
    return null;
  }
  const normalized = normalizePagesHyperthreadPackages(parsed);
  return normalized.length > 0 ? normalized : null;
};

export const mergePagesExecutePassThroughVariables = (
  toFieldMappingOutputs,
  updatePayload,
  passThroughNames = PAGES_GET_RATES_PASS_THROUGH_VARIABLES
) => {
  const merged = { ...(toFieldMappingOutputs || {}) };
  getPagesExecuteMappingList(updatePayload).forEach((mapping) => {
    const name = mapping?.pages_variable_name;
    const nameLower = String(name || "").toLowerCase();
    const isPackageMapping =
      name === PAGES_PACKAGE_PASS_THROUGH_VARIABLE ||
      nameLower === "package" ||
      nameLower === "packages";
    if (
      !name ||
      (!passThroughNames.includes(name) && !isPackageMapping)
    ) {
      return;
    }
    const val = mapping?.selected_value;
    if (val == null || val === "") {
      return;
    }

    if (isPackageMapping) {
      const packages = parsePagesPackagePassThroughValue(val);
      if (packages) {
        const existing = merged.Package;
        const stepRowsKeepLookup =
          Array.isArray(existing) &&
          existing.some(
            (row) =>
              row &&
              typeof row === "object" &&
              row.cf_packages != null &&
              typeof row.cf_packages === "object" &&
              !Array.isArray(row.cf_packages)
          );
        if (!stepRowsKeepLookup) {
          const displayRows = mapPackagesForRatesDisplay(packages);
          merged.Package = displayRows;
          merged.Package_out = displayRows;
        }
      }
      return;
    }

    if (name === "package_type" || name === "Package / Pallet") {
      const scalar = normalizePicklistPackageType(val);
      if (scalar) {
        merged.package_type = scalar;
      }
      return;
    }

    if (
      name === "to_additional_services" ||
      name === "from_additional_services"
    ) {
      let parsed = parseAccidentalJsonString(val);
      if (!Array.isArray(parsed)) {
        parsed = parsePagesResponseJsonIfNeeded(parsed);
      }
      if (Array.isArray(parsed)) {
        merged[name] = normalizePagesAdditionalServices(parsed);
      } else {
        merged[name] = val;
      }
    }
  });
  return merged;
};

const pad2PagesDate = (n) => String(n).padStart(2, "0");

const startOfLocalDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const formatPagesIsoLocalDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }
  return `${date.getFullYear()}-${pad2PagesDate(date.getMonth() + 1)}-${pad2PagesDate(
    date.getDate()
  )}`;
};

export const parsePagesShipmentDateValue = (raw) => {
  if (raw == null || raw === "") {
    return null;
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    if (raw.value != null && raw.value !== "") {
      return parsePagesShipmentDateValue(raw.value);
    }
    if (raw.name != null && raw.name !== "") {
      return parsePagesShipmentDateValue(raw.name);
    }
  }
  const text = String(raw).trim();
  if (!text || text.toLowerCase() === "null") {
    return null;
  }
  let match = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (match) {
    const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(d.getTime()) ? null : startOfLocalDay(d);
  }
  match = text.match(/^(\d{1,2})-(\d{1,2})-(\d{2,4})/);
  if (match) {
    let year = Number(match[3]);
    if (year < 100) {
      year += 2000;
    }
    const d = new Date(year, Number(match[2]) - 1, Number(match[1]));
    return Number.isNaN(d.getTime()) ? null : startOfLocalDay(d);
  }
  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) {
    return startOfLocalDay(parsed);
  }
  return null;
};

/**
 * Stale SBR/order dates break carrier APIs; layout get-rates uses today when past/invalid.
 * Format with user date preference so DatePick does not show "Invalid date".
 */
export const normalizePagesRatingsShipmentDateForExecute = (value) => {
  const today = startOfLocalDay(new Date());
  const parsed = parsePagesShipmentDateValue(value);
  const useDate = parsed == null || parsed < today ? today : parsed;
  let userFormat = "YYYY-MM-DD";
  try {
    userFormat = getDateFormat("Date") || "YYYY-MM-DD";
  } catch (e) {
    userFormat = "YYYY-MM-DD";
  }
  const iso = formatPagesIsoLocalDate(useDate);
  if (!iso) {
    return null;
  }
  if (userFormat === "YYYY-MM-DD") {
    return iso;
  }
  // Reuse ISO parts → user display format without Moment dependency here.
  const [y, m, d] = iso.split("-");
  return userFormat
    .replace(/YYYY/g, y)
    .replace(/YY/g, String(y).slice(-2))
    .replace(/MM/g, m)
    .replace(/DD/g, d);
};

const PAGES_SHIPMENT_DATE_OUTPUT_KEYS = [
  "shipment_date",
  "Shipment_date",
  "Shipment Date",
];

/** After SBR/ref, layout shows today on Add Shipment; order ship dates must not stick on Pages. */
export const applyPagesLayoutShipmentDateParity = (outputs = {}) => {
  const next = { ...outputs };
  PAGES_SHIPMENT_DATE_OUTPUT_KEYS.forEach((key) => {
    if (next[key] == null || next[key] === "") {
      return;
    }
    next[key] = normalizePagesRatingsShipmentDateForExecute(next[key]);
  });
  ["shipObj", "ship_obj"].forEach((key) => {
    if (!Array.isArray(next[key])) {
      return;
    }
    next[key] = next[key].map((row) => {
      if (row == null || typeof row !== "object") {
        return row;
      }
      const raw =
        row.shipment_date ?? row["Shipment Date"] ?? row.Shipment_date;
      if (raw == null || raw === "") {
        return row;
      }
      const normalized = normalizePagesRatingsShipmentDateForExecute(raw);
      return {
        ...row,
        shipment_date: normalized,
        Shipment_date: normalized,
        "Shipment Date": normalized,
      };
    });
  });
  return next;
};

/** Map pages-config execute mappings → fieldFormPayload shape for rates merge. */
export const buildFieldFormPayloadFromExecuteMappings = (executeMappings) => {
  const out = {};
  getPagesExecuteMappingList(executeMappings).forEach((mapping) => {
    const name = mapping?.pages_variable_name;
    if (!name) {
      return;
    }
    out[name] = { selected_value: mapping?.selected_value };
  });
  return out;
};

export const buildPagesRedirectFieldOutputs = (toFieldMapping, updatePayload) => {
  const outputs = {};
  (toFieldMapping || []).forEach((v) => {
    if (v?.pages_variable_name != null) {
      outputs[v.pages_variable_name] = v?.value;
    }
  });
  const passThroughNames = [...PAGES_GET_RATES_PASS_THROUGH_VARIABLES];
  const merged = mergePagesExecutePassThroughVariables(
    outputs,
    updatePayload,
    passThroughNames
  );
  const withShipObj = mergePagesExecuteIntoShipObj(merged, updatePayload);
  return normalizePagesGetRatesRedirectOutputs(withShipObj);
};

export const applyGetRatesFormatToExecuteValue = (
  finalValue,
  apiName,
  automationType,
  isArrayOrObjectPagesVariable,
  isLookupPagesVariable
) => {
  const apiLower = String(apiName || "").toLowerCase();
  let value = finalValue;

  if (
    automationType === "RATINGS" &&
    (apiName === "Package" ||
      apiName === "Packages" ||
      apiLower === "package" ||
      apiLower === "packages") &&
    Array.isArray(value)
  ) {
    value = normalizePagesHyperthreadPackages(value);
  }

  if (
    automationType === "RATINGS" &&
    apiLower.includes("additional_service") &&
    Array.isArray(value)
  ) {
    value = normalizePagesAdditionalServices(value);
  }

  if (
    automationType === "RATINGS" &&
    (apiLower === "shipment_date" || apiLower === "shipment date")
  ) {
    return normalizePagesRatingsShipmentDateForExecute(value);
  }

  if (
    automationType === "RATINGS" &&
    isArrayOrObjectPagesVariable &&
    !isLookupPagesVariable &&
    value != null &&
    value !== ""
  ) {
    return JSON.stringify(Array.isArray(value) ? value : [value]);
  }

  return value;
};
